import { useState } from "react";
import { KeyRound, Phone, Mail, MapPin, Clock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PHONE_DISPLAY, PHONE_TEL, EMAIL, EMAIL_MAILTO, SERVICE_AREAS, NAV_LINKS, scrollToId } from "@/site";

const LEGAL = {
    privacy: {
        title: "Privacy Policy",
        body: [
            "Regina Expert Locksmith respects your privacy. When you contact us or submit a quote request, we collect only the information needed to respond to your request — typically your name, phone number, and details about the service you need.",
            "We do not sell, rent, or share your personal information with third parties for marketing purposes. Information you provide is used solely to communicate with you about your locksmith service request.",
            "If you have questions about how your information is handled, contact us at reginaexpertlocksmith@gmail.com.",
        ],
    },
    terms: {
        title: "Terms of Service",
        body: [
            "Regina Expert Locksmith provides residential, commercial, automotive, and emergency locksmith services in Regina, Saskatchewan and surrounding communities.",
            "Pricing depends on the type of service, parts, time, and complexity of the job. Wherever possible, we explain pricing before work begins. Response times depend on location, traffic, and current call volume; any estimate we provide is given in good faith and is not a guarantee.",
            "By using this website or contacting us, you agree to provide accurate information so we can serve you properly. Questions? Call 306-262-5995 or email reginaexpertlocksmith@gmail.com.",
        ],
    },
};

export const Footer = () => {
    const [legal, setLegal] = useState(null);

    return (
        <footer data-testid="site-footer" className="border-t border-white/10 bg-navy-950 pb-24 md:pb-0">
            <div className="mx-auto grid w-full max-w-7xl gap-12 px-5 py-16 sm:px-8 md:grid-cols-2 lg:grid-cols-4">
                <div>
                    <div className="flex items-center gap-3">
                        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gold text-navy-950">
                            <KeyRound className="h-5 w-5" />
                        </span>
                        <span className="font-display text-lg font-semibold text-white">Regina Expert Locksmith</span>
                    </div>
                    <p className="mt-4 text-sm leading-relaxed text-slate-400">
                        24/7 Residential • Commercial • Automotive Locksmith Services
                    </p>
                    <p className="mt-2 text-sm text-slate-500">Family-owned and locally operated in Regina, SK.</p>
                </div>

                <div>
                    <h3 className="text-xs font-bold uppercase tracking-[0.22em] text-gold">Contact</h3>
                    <ul className="mt-4 space-y-3 text-sm">
                        <li>
                            <a data-testid="footer-phone" href={PHONE_TEL} className="flex items-center gap-2.5 text-slate-300 transition-colors hover:text-gold">
                                <Phone className="h-4 w-4 text-gold" />
                                {PHONE_DISPLAY}
                            </a>
                        </li>
                        <li>
                            <a data-testid="footer-email" href={EMAIL_MAILTO} className="flex items-center gap-2.5 break-all text-slate-300 transition-colors hover:text-gold">
                                <Mail className="h-4 w-4 shrink-0 text-gold" />
                                {EMAIL}
                            </a>
                        </li>
                        <li className="flex items-start gap-2.5 text-slate-300">
                            <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                            Service Area: {SERVICE_AREAS.join(", ")}, SK
                        </li>
                        <li className="flex items-center gap-2.5 text-slate-300">
                            <Clock className="h-4 w-4 text-gold" />
                            Business Hours: 24/7
                        </li>
                    </ul>
                </div>

                <div>
                    <h3 className="text-xs font-bold uppercase tracking-[0.22em] text-gold">Quick Links</h3>
                    <ul className="mt-4 space-y-2.5 text-sm">
                        <li>
                            <button data-testid="footer-link-home" onClick={() => scrollToId("home")} className="text-slate-300 transition-colors hover:text-gold">
                                Home
                            </button>
                        </li>
                        {NAV_LINKS.map((l) => (
                            <li key={l.id}>
                                <button
                                    data-testid={`footer-link-${l.id}`}
                                    onClick={() => scrollToId(l.id)}
                                    className="text-slate-300 transition-colors hover:text-gold"
                                >
                                    {l.label}
                                </button>
                            </li>
                        ))}
                        <li>
                            <button data-testid="footer-link-contact" onClick={() => scrollToId("contact")} className="text-slate-300 transition-colors hover:text-gold">
                                Contact
                            </button>
                        </li>
                    </ul>
                </div>

                <div>
                    <h3 className="text-xs font-bold uppercase tracking-[0.22em] text-gold">Legal</h3>
                    <ul className="mt-4 space-y-2.5 text-sm">
                        <li>
                            <button data-testid="footer-link-privacy" onClick={() => setLegal("privacy")} className="text-slate-300 transition-colors hover:text-gold">
                                Privacy Policy
                            </button>
                        </li>
                        <li>
                            <button data-testid="footer-link-terms" onClick={() => setLegal("terms")} className="text-slate-300 transition-colors hover:text-gold">
                                Terms of Service
                            </button>
                        </li>
                    </ul>
                    <p className="mt-6 text-xs leading-relaxed text-slate-500">
                        Locked out right now? Don't wait — call us any time, day or night.
                    </p>
                </div>
            </div>

            <div className="border-t border-white/10">
                <div className="mx-auto flex w-full max-w-7xl flex-col gap-2 px-5 py-6 text-xs text-slate-500 sm:flex-row sm:items-center sm:justify-between sm:px-8">
                    <p>© 2026 Regina Expert Locksmith. All rights reserved.</p>
                    <p>Regina, Saskatchewan • Available 24/7</p>
                </div>
            </div>

            <Dialog open={!!legal} onOpenChange={() => setLegal(null)}>
                <DialogContent data-testid="legal-modal" className="rounded-2xl sm:max-w-md">
                    {legal && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="font-display text-2xl font-semibold text-slate-900">
                                    {LEGAL[legal].title}
                                </DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 text-sm leading-relaxed text-slate-600">
                                {LEGAL[legal].body.map((p) => (
                                    <p key={p.slice(0, 24)}>{p}</p>
                                ))}
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </footer>
    );
};
